import { Calendar, CheckCircle, FileText, Receipt, Wallet } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const STATUS_LABELS = {
  pending: '청구대기',
  issued: '발행완료',
  paid: '수납완료',
  overdue: '연체',
  cancelled: '취소'
};

const STATUS_CLASS = {
  pending: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-700'
};

const paymentMethods = [
  { value: 'bank_transfer', label: '계좌이체' },
  { value: 'cash', label: '현금' },
  { value: 'card', label: '카드' },
  { value: 'cms', label: 'CMS' },
  { value: 'other', label: '기타' }
];

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

const getMonthPeriod = (billingMonth) => {
  const [year, month] = billingMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return {
    period_start: `${billingMonth}-01`,
    period_end: `${billingMonth}-${String(lastDay).padStart(2, '0')}`
  };
};

const formatCurrency = (value) => `${Number(value || 0).toLocaleString()}원`;

const normalizeOwners = (response) => {
  if (response?.property_owners) return response.property_owners;
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const ManagementServiceBilling = () => {
  const [billingMonth, setBillingMonth] = useState(getCurrentMonth());
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [paymentBill, setPaymentBill] = useState(null);
  const [manualBillForm, setManualBillForm] = useState({
    property_owner_id: '',
    additional_amount: '0',
    discount_amount: '0',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'bank_transfer',
    payment_reference: ''
  });
  const queryClient = useQueryClient();

  const { data: propertyOwnersData } = useQuery(
    'rmc-property-owners',
    async () => normalizeOwners(await api.get('/rmc/property-owners')),
    {
      onError: () => toast.error('임대사업자 목록을 불러오지 못했습니다.')
    }
  );

  const propertyOwners = propertyOwnersData || [];

  const { data: billsData, isLoading: billsLoading } = useQuery(
    ['management-service-bills', billingMonth, selectedPropertyOwner, selectedStatus],
    async () => {
      const params = {
        billing_month: billingMonth || undefined,
        property_owner_id: selectedPropertyOwner || undefined,
        status: selectedStatus || undefined
      };
      return api.get('/management-service-billing/bills', { params });
    },
    {
      onError: () => toast.error('청구내역을 불러오지 못했습니다.')
    }
  );

  const bills = useMemo(() => billsData?.bills || [], [billsData]);

  const summary = useMemo(() => {
    const totalAmount = bills.reduce((sum, bill) => sum + Number(bill.total_amount || 0), 0);
    const paidAmount = bills
      .filter((bill) => bill.status === 'paid')
      .reduce((sum, bill) => sum + Number(bill.total_amount || 0), 0);
    return {
      count: bills.length,
      totalAmount,
      paidAmount,
      unpaidAmount: totalAmount - paidAmount,
      paidCount: bills.filter((bill) => bill.status === 'paid').length
    };
  }, [bills]);

  const refetchBills = () => {
    queryClient.invalidateQueries('management-service-bills');
  };

  const createBillMutation = useMutation(
    (payload) => api.post('/management-service-billing/bills', payload),
    {
      onSuccess: () => {
        toast.success('청구서를 생성했습니다.');
        setShowCreateModal(false);
        refetchBills();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '청구서 생성에 실패했습니다.');
      }
    }
  );

  const bulkCreateMutation = useMutation(
    async () => {
      const targets = selectedPropertyOwner
        ? propertyOwners.filter((owner) => String(owner.id) === String(selectedPropertyOwner))
        : propertyOwners;
      const period = getMonthPeriod(billingMonth);
      const results = [];

      for (const owner of targets) {
        try {
          const response = await api.post('/management-service-billing/bills', {
            property_owner_id: owner.id,
            service_plan_id: null,
            billing_month: billingMonth,
            ...period,
            additional_amount: 0,
            discount_amount: 0,
            notes: '월말 기준 일괄 청구 생성'
          });
          results.push({ owner, success: true, response });
        } catch (error) {
          results.push({ owner, success: false, error });
        }
      }

      return results;
    },
    {
      onSuccess: (results) => {
        const successCount = results.filter((result) => result.success).length;
        const failCount = results.length - successCount;
        if (failCount > 0) {
          toast.error(`일괄 청구 ${successCount}건 생성, ${failCount}건 실패`);
        } else {
          toast.success(`일괄 청구 ${successCount}건을 생성했습니다.`);
        }
        refetchBills();
      }
    }
  );

  const issueBillMutation = useMutation(
    (billId) => api.post(`/management-service-billing/bills/${billId}/issue`),
    {
      onSuccess: () => {
        toast.success('청구서를 발행했습니다.');
        refetchBills();
      },
      onError: (error) => toast.error(error.response?.data?.error || '청구서 발행에 실패했습니다.')
    }
  );

  const payBillMutation = useMutation(
    ({ billId, paymentData }) => api.post(`/management-service-billing/bills/${billId}/pay`, paymentData),
    {
      onSuccess: () => {
        toast.success('수납처리했습니다.');
        setPaymentBill(null);
        setPaymentForm({ payment_method: 'bank_transfer', payment_reference: '' });
        refetchBills();
      },
      onError: (error) => toast.error(error.response?.data?.error || '수납처리에 실패했습니다.')
    }
  );

  const handleManualCreate = () => {
    if (!manualBillForm.property_owner_id) {
      toast.error('임대사업자를 선택해주세요.');
      return;
    }
    const period = getMonthPeriod(billingMonth);
    createBillMutation.mutate({
      property_owner_id: parseInt(manualBillForm.property_owner_id, 10),
      service_plan_id: null,
      billing_month: billingMonth,
      ...period,
      additional_amount: parseFloat(manualBillForm.additional_amount || 0),
      discount_amount: parseFloat(manualBillForm.discount_amount || 0),
      notes: manualBillForm.notes
    });
  };

  const handleBulkCreate = () => {
    if (!billingMonth) {
      toast.error('청구월을 선택해주세요.');
      return;
    }
    const targetLabel = selectedPropertyOwner ? '선택한 임대사업자' : '전체 임대사업자';
    if (window.confirm(`${billingMonth} 월말 기준으로 ${targetLabel} 청구서를 생성할까요?\n유지보수 관리자 대납 청구대기건은 추가 청구액에 자동 포함됩니다.`)) {
      bulkCreateMutation.mutate();
    }
  };

  const handlePay = () => {
    if (!paymentBill) return;
    payBillMutation.mutate({
      billId: paymentBill.id,
      paymentData: paymentForm
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">수수료청구/수납처리</h2>
        <p className="mt-1 text-sm text-gray-600">
          월말 기준 임대관리수수료 청구서를 생성하고 발행, 수납 상태를 관리합니다.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard icon={FileText} label="청구 건수" value={`${summary.count}건`} />
        <SummaryCard icon={Receipt} label="총 청구액" value={formatCurrency(summary.totalAmount)} />
        <SummaryCard icon={CheckCircle} label="수납완료" value={`${summary.paidCount}건 / ${formatCurrency(summary.paidAmount)}`} tone="green" />
        <SummaryCard icon={Wallet} label="미수금" value={formatCurrency(summary.unpaidAmount)} tone="red" />
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">청구월</label>
            <input
              type="month"
              value={billingMonth}
              onChange={(e) => setBillingMonth(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">임대사업자</label>
            <select
              value={selectedPropertyOwner}
              onChange={(e) => setSelectedPropertyOwner(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">전체</option>
              {propertyOwners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.company_name || owner.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">수납상태</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">전체</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-2 flex flex-col sm:flex-row gap-2 lg:items-end">
            <button
              type="button"
              onClick={handleBulkCreate}
              disabled={bulkCreateMutation.isLoading || propertyOwners.length === 0}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {bulkCreateMutation.isLoading ? '일괄 생성 중...' : '월말 일괄 청구 생성'}
            </button>
            <button
              type="button"
              onClick={() => {
                setManualBillForm({
                  property_owner_id: selectedPropertyOwner || '',
                  additional_amount: '0',
                  discount_amount: '0',
                  notes: ''
                });
                setShowCreateModal(true);
              }}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              개별 청구 생성
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {billsLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">청구내역을 불러오는 중...</div>
        ) : bills.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">청구내역이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">선택한 월의 일괄 청구를 생성해보세요.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">청구번호</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">임대사업자</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">청구기간</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">활성/전체</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">청구액</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">처리</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {bill.bill_number}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bill.property_owner_company_name || bill.property_owner_name || '-'}
                      {bill.service_plan_name && (
                        <div className="text-xs text-gray-500">{bill.service_plan_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bill.period_start} ~ {bill.period_end}
                      <div className="text-xs text-gray-400">납부기한 {bill.due_date || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {bill.active_units || 0} / {bill.total_units || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(bill.total_amount)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[bill.status] || STATUS_CLASS.pending}`}>
                        {STATUS_LABELS[bill.status] || bill.status}
                      </span>
                      {bill.paid_at && <div className="mt-1 text-xs text-gray-500">수납 {bill.paid_at.slice(0, 10)}</div>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {bill.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => issueBillMutation.mutate(bill.id)}
                          disabled={issueBillMutation.isLoading}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          발행
                        </button>
                      )}
                      {['issued', 'overdue'].includes(bill.status) && (
                        <button
                          type="button"
                          onClick={() => setPaymentBill(bill)}
                          className="text-green-600 hover:text-green-900"
                        >
                          수납처리
                        </button>
                      )}
                      {bill.status === 'paid' && <span className="text-gray-400">완료</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <BillCreateModal
          billingMonth={billingMonth}
          propertyOwners={propertyOwners}
          formData={manualBillForm}
          setFormData={setManualBillForm}
          isLoading={createBillMutation.isLoading}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleManualCreate}
        />
      )}

      {paymentBill && (
        <PaymentModal
          bill={paymentBill}
          formData={paymentForm}
          setFormData={setPaymentForm}
          isLoading={payBillMutation.isLoading}
          onClose={() => setPaymentBill(null)}
          onSubmit={handlePay}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, tone = 'blue' }) => {
  const toneClass = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600'
  }[tone] || 'bg-blue-100 text-blue-600';

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${toneClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="ml-3 min-w-0">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-lg font-semibold text-gray-900 truncate">{value}</p>
        </div>
      </div>
    </div>
  );
};

const BillCreateModal = ({ billingMonth, propertyOwners, formData, setFormData, isLoading, onClose, onSubmit }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
      <h3 className="text-lg font-medium text-gray-900 mb-4">개별 청구 생성</h3>
      <div className="space-y-4">
        <div className="text-sm text-gray-600">청구월: {billingMonth}</div>
        <div>
          <label className="block text-sm font-medium text-gray-700">임대사업자 *</label>
          <select
            value={formData.property_owner_id}
            onChange={(e) => setFormData({ ...formData, property_owner_id: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">선택</option>
            {propertyOwners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.company_name || owner.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">수동 추가 청구액</label>
            <input
              type="number"
              min="0"
              value={formData.additional_amount}
              onChange={(e) => setFormData({ ...formData, additional_amount: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">할인액</label>
            <input
              type="number"
              min="0"
              value={formData.discount_amount}
              onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">메모</label>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-4 mt-4 border-t">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200">
          취소
        </button>
        <button type="button" onClick={onSubmit} disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50">
          {isLoading ? '생성 중...' : '청구 생성'}
        </button>
      </div>
    </div>
  </div>
);

const PaymentModal = ({ bill, formData, setFormData, isLoading, onClose, onSubmit }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
      <h3 className="text-lg font-medium text-gray-900 mb-4">수납처리</h3>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700">
          <div>청구번호: {bill.bill_number}</div>
          <div>임대사업자: {bill.property_owner_company_name || bill.property_owner_name || '-'}</div>
          <div className="font-semibold text-gray-900">수납금액: {formatCurrency(bill.total_amount)}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">수납방법</label>
          <select
            value={formData.payment_method}
            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          >
            {paymentMethods.map((method) => (
              <option key={method.value} value={method.value}>{method.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">입금/승인 참조번호</label>
          <input
            type="text"
            value={formData.payment_reference}
            onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="선택 입력"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-4 mt-4 border-t">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200">
          취소
        </button>
        <button type="button" onClick={onSubmit} disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50">
          {isLoading ? '처리 중...' : '수납 완료'}
        </button>
      </div>
    </div>
  </div>
);

export default ManagementServiceBilling;
